import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui'
import { FileText, Clock, Zap, Shield, MessageSquare, X, Search } from 'lucide-react'

const changes = [
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'Temp Mail Without Login',
    description: 'Guest users can now create temporary email addresses without requiring account authentication.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'Auto-Refresh for All Users',
    description: 'Fixed auto-refresh functionality to work for both authenticated users and guest users.',
    adminOnly: true,
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'reCAPTCHA v3 Protection',
    description: 'Enhanced security with Google reCAPTCHA v3 for login and registration.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'Search Functionality',
    description: 'Added tool search with suggestions in header for quick access.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'Dynamic Popular Tools',
    description: 'Popular tools are now automatically identified and displayed on homepage.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'feature',
    title: 'Changelog Page',
    description: 'Added dedicated changelog page to view all updates.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Removed Ready Badge',
    description: 'Cleaned up tool badges by removing "Ready" tag.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Developer Tools Expansion',
    description: 'Fixed issue where Developer Tools category was expanded on page load.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Sidebar Scroll Preservation',
    description: 'Improved scroll position tracking to prevent jump when navigating between tools.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Sidebar Lock Icon',
    description: 'Removed lock icon from Temp Mail navigation item.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Mobile Search Dropdown',
    description: 'Fixed search dropdown positioning on mobile devices with full-screen overlay.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'fix',
    title: 'Hidden VIP Pages',
    description: 'Removed VIP pricing page from navigation and links.',
  },
  {
    version: 'v3.5',
    date: '2026-02-23',
    type: 'change',
    title: 'Access Level Text',
    description: 'Changed access level display from "user / member / admin" to "user / member".',
  },
]

export function Changelog() {
  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Changelog</h1>
                <p className="text-muted-foreground">All changes and updates to SCB Terminal</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {changes.map((change, index) => (
              <Card key={index} className="glass">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <CardTitle className="flex items-center gap-3">
                      {change.type === 'feature' && <Zap className="h-5 w-5 text-blue-400" />}
                      {change.type === 'fix' && <Clock className="h-5 w-5 text-green-400" />}
                      {change.type === 'change' && <MessageSquare className="h-5 w-5 text-orange-400" />}
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-mono px-2 py-1 rounded ${
                        change.type === 'feature' ? 'bg-blue-500/20 text-blue-400' :
                        change.type === 'fix' ? 'bg-green-500/20 text-green-400' :
                        change.type === 'change' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {change.version}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{change.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="glass border-primary/30 mt-6 p-6">
          <h2 className="text-xl font-semibold mb-4">Version v3.5 Highlights</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent>
                <h3 className="font-semibold mb-2">What's New</h3>
                <ul className="space-y-2 text-sm text-muted-foreground list-disc ml-4">
                  <li>Temp Mail now available to <strong className="text-foreground">guest users</strong> without login</li>
                  <li>Auto-refresh now works for <strong className="text-foreground">all users</strong></li>
                  <li>reCAPTCHA v3 protection on login and registration</li>
                  <li>Search functionality in header</li>
                  <li>Dynamic popular tools on homepage</li>
                  <li>Added dedicated changelog page</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h3 className="font-semibold mb-2">Bug Fixes</h3>
                <ul className="space-y-2 text-sm text-muted-foreground list-disc ml-4">
                  <li>Removed "Ready" badge</li>
                  <li>Developer Tools starts collapsed</li>
                  <li>Improved sidebar scroll tracking</li>
                  <li>Removed lock icon from Temp Mail navigation</li>
                  <li>Fixed mobile search dropdown</li>
                  <li>Removed VIP pricing pages</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
